import React, { useMemo, useRef, useState } from "react";
import { FileText, X, AlertTriangle, CheckCircle, Download, User, Building2, Home, CreditCard, DollarSign, PieChart, ListChecks, FileSearch } from "lucide-react";
import html2pdf from "html2pdf.js";
import Logo from "./Logo";

const getSection = (data, keys) => {
  for (const key of keys) {
    if (data && Object.prototype.hasOwnProperty.call(data, key)) {
      return data[key];
    }
  }
  return null;
};

const toList = (val) => (Array.isArray(val) ? val : []);

const SectionCard = ({ id, title, summary, keyInsights, warnings, markdown, icon }) => (
  <div className="border border-slate-200 rounded-xl p-5 bg-white shadow-sm space-y-3">
    <div className="flex items-center gap-3" id={id}>
      <div className="w-9 h-9 rounded-lg bg-slate-900 text-white flex items-center justify-center">
        {icon}
      </div>
      <div>
        <h3 className="text-lg font-bold text-slate-900">{title}</h3>
        {summary && <p className="text-sm text-slate-600">{summary}</p>}
      </div>
    </div>
    {(keyInsights?.length || warnings?.length) && (
      <div className="grid md:grid-cols-2 gap-4 text-sm">
        {keyInsights?.length ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="flex items-center gap-2 text-emerald-700 font-semibold mb-2">
              <CheckCircle className="w-4 h-4" />
              Key Insights
            </div>
            <ul className="space-y-1 text-emerald-900">
              {keyInsights.map((item, idx) => (
                <li key={`${title}-insight-${idx}`}>{item}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {warnings?.length ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-center gap-2 text-yellow-700 font-semibold mb-2">
              <AlertTriangle className="w-4 h-4" />
              Warnings
            </div>
            <ul className="space-y-1 text-yellow-900">
              {warnings.map((item, idx) => (
                <li key={`${title}-warning-${idx}`}>{item}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    )}
    {markdown && (
      <div className="text-sm text-slate-700 whitespace-pre-line border-t border-slate-200 pt-3">
        {markdown}
      </div>
    )}
  </div>
);

const UnderwritingReportModal = ({ isOpen, onClose, reportData }) => {
  const reportRef = useRef(null);
  const [activeSection, setActiveSection] = useState(null);
  const meta = getSection(reportData, ["0_meta_data", "0) Meta Data", "0) Meta data"]) || {};
  const execSummary =
    getSection(reportData, ["1_executive_summary", "1) Executive Summary", "1) Executive summary"]) || "";

  const borrowers = getSection(reportData, ["2_borrowers_info", "2) Borrowers Info", "2) Borrowers info"]) || {};
  const business = getSection(reportData, ["3_business_entity_analysis", "3) Business Entity Analysis", "3) Business entity analysis"]) || {};
  const reo = getSection(reportData, ["4_real_estate_owned", "4) Real Estate Owned", "4) Real estate owned"]) || {};
  const liabilities = getSection(reportData, ["5_liabilities", "5) Liabilities", "5) liabilities"]) || {};
  const income = getSection(reportData, ["6_income_summary", "6) Income Summary", "6) Income summary"]) || {};
  const reserves = getSection(reportData, ["7_reserves_dti", "7) Reserves & DTI", "7) Reserves and DTI"]) || {};
  const risks = getSection(reportData, ["8_risk_observations", "8) Risk Observations", "8) Risk observations"]) || {};
  const nextSteps = getSection(reportData, ["9_next_steps_conditions", "9) Next Steps / Conditions", "9) Next Steps", "9) Next steps / conditions"]) || {};
  const auditing = getSection(reportData, ["10_auditing", "10) Auditing", "10) Audit"]) || {};
  const forensic = getSection(reportData, ["11_forensic_accounting", "11) Forensic Accounting", "11) Forensic accounting"]) || {};
  const references = getSection(reportData, ["12_references", "12) References", "12) Reference"]) || [];

  const getSectionIcon = (title) => {
    const t = title.toLowerCase();
    if (t.includes("borrower")) return <User className="w-4 h-4" />;
    if (t.includes("business")) return <Building2 className="w-4 h-4" />;
    if (t.includes("estate") || t.includes("property")) return <Home className="w-4 h-4" />;
    if (t.includes("liabilities") || t.includes("debt")) return <CreditCard className="w-4 h-4" />;
    if (t.includes("income")) return <DollarSign className="w-4 h-4" />;
    if (t.includes("reserves") || t.includes("dti") || t.includes("asset")) return <PieChart className="w-4 h-4" />;
    if (t.includes("risk") || t.includes("findings")) return <AlertTriangle className="w-4 h-4" />;
    if (t.includes("step") || t.includes("condition")) return <ListChecks className="w-4 h-4" />;
    if (t.includes("reference") || t.includes("audit")) return <FileSearch className="w-4 h-4" />;
    if (t.includes("executive")) return <FileText className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  const tableOfContents = useMemo(() => ([
    { id: "toc-executive", label: "Executive Summary" },
    { id: "toc-borrowers", label: "Borrowers Info" },
    { id: "toc-business", label: "Business Entity Analysis" },
    { id: "toc-reo", label: "Real Estate Owned" },
    { id: "toc-liabilities", label: "Liabilities" },
    { id: "toc-income", label: "Income Summary" },
    { id: "toc-reserves", label: "Reserves & DTI" },
    { id: "toc-risks", label: "Risk Observations" },
    { id: "toc-next-steps", label: "Next Steps / Conditions" },
    { id: "toc-auditing", label: "Auditing" },
    { id: "toc-forensic", label: "Forensic Accounting" },
    { id: "toc-references", label: "References" }
  ]), []);

  const handleDownload = () => {
    if (!reportRef.current) return;
    const fileName = `Underwriting_Report_${meta.report_date || "Latest"}.pdf`;
    const options = {
      margin: 0.5,
      filename: fileName,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
      pagebreak: { mode: ["css", "legacy"], avoid: ".pdf-avoid-break" }
    };
    html2pdf().from(reportRef.current).set(options).save();
  };

  const scrollTo = (id) => {
    const el = reportRef.current?.querySelector(`#${id}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveSection(id);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 sm:p-6 dark:bg-slate-900/80 bg-slate-500/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-5xl h-[95vh] dark:bg-slate-900 bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border dark:border-slate-800 border-slate-200"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-800 border-slate-200 dark:bg-slate-900 bg-slate-50 shrink-0">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-slate-900 rounded-lg">
              <Logo className="h-8 w-auto" />
            </div>
            <div>
              <h3 className="font-bold text-lg dark:text-white text-slate-800">A.I. Underwriting Report</h3>
              <p className="text-sm text-slate-500">
                {meta.model_used ? `Model: ${meta.model_used}` : "Generated Report"}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-slate-500 dark:hover:text-white hover:text-slate-900 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 flex h-full overflow-hidden dark:bg-slate-950 bg-slate-50/50">
          <div className="w-64 border-r dark:border-slate-800 border-slate-200 dark:bg-slate-900 bg-white flex flex-col">
            <div className="p-4 border-b dark:border-slate-800 border-slate-100">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Sections</p>
            </div>
            <div className="flex-1 overflow-y-auto py-2">
              {tableOfContents.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollTo(item.id)}
                  className={`w-full text-left px-5 py-2.5 text-sm font-medium flex items-center gap-3 transition-colors
                    ${activeSection === item.id
                      ? "dark:bg-yellow-900/20 bg-yellow-50 dark:text-yellow-400 text-yellow-700 border-r-4 dark:border-yellow-500 border-yellow-600"
                      : "dark:text-slate-400 text-slate-600 dark:hover:bg-slate-800 hover:bg-slate-50 dark:hover:text-slate-200 hover:text-slate-900"
                    }`}
                >
                  <span className={activeSection === item.id ? "dark:text-yellow-400 text-yellow-600" : "text-slate-400"}>
                    {getSectionIcon(item.label)}
                  </span>
                  <span className="truncate">{item.label}</span>
                </button>
              ))}
            </div>
            <div className="p-4 border-t dark:border-slate-800 border-slate-100 dark:bg-slate-900 bg-slate-50">
              <button
                onClick={handleDownload}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-lg text-sm font-semibold hover:bg-slate-700 transition"
              >
                <Download className="w-4 h-4" /> Download PDF
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            <div ref={reportRef} className="p-6 space-y-6">
              <div className="border border-slate-200 rounded-xl p-6 bg-slate-50" id="toc-executive">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Executive Summary</h2>
                    <p className="text-sm text-slate-500">
                      {meta.report_date ? `Report Date: ${meta.report_date}` : "Report Date: N/A"}
                      {meta.version ? ` • Version ${meta.version}` : ""}
                    </p>
                  </div>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                  {execSummary || "No executive summary available."}
                </p>
              </div>

              <SectionCard
                id="toc-borrowers"
                title="Borrowers Info"
                summary={borrowers.summary}
                keyInsights={toList(borrowers.key_insight)}
                warnings={toList(borrowers.warnings)}
                markdown={borrowers.markdown}
                icon={getSectionIcon("Borrowers")}
              />
              <SectionCard
                id="toc-business"
                title="Business Entity Analysis"
                summary={business.summary}
                keyInsights={toList(business.key_insight)}
                warnings={toList(business.warnings)}
                markdown={business.markdown}
                icon={getSectionIcon("Business")}
              />
              <SectionCard
                id="toc-reo"
                title="Real Estate Owned"
                summary={reo.summary}
                keyInsights={toList(reo.key_insight)}
                warnings={toList(reo.warnings)}
                markdown={reo.markdown}
                icon={getSectionIcon("Real Estate")}
              />
              <SectionCard
                id="toc-liabilities"
                title="Liabilities"
                summary={liabilities.summary}
                keyInsights={toList(liabilities.key_insight)}
                warnings={toList(liabilities.warnings)}
                markdown={liabilities.markdown}
                icon={getSectionIcon("Liabilities")}
              />
              <SectionCard
                id="toc-income"
                title="Income Summary"
                summary={income.summary}
                keyInsights={toList(income.key_insight)}
                warnings={toList(income.warnings)}
                markdown={income.markdown}
                icon={getSectionIcon("Income")}
              />
              <SectionCard
                id="toc-reserves"
                title="Reserves & DTI"
                summary={reserves.summary}
                keyInsights={toList(reserves.key_insight)}
                warnings={toList(reserves.warnings)}
                markdown={reserves.markdown}
                icon={getSectionIcon("Reserves")}
              />
              <SectionCard
                id="toc-risks"
                title="Risk Observations"
                summary={risks.summary}
                keyInsights={toList(risks.key_insight)}
                warnings={toList(risks.warnings)}
                markdown={risks.markdown}
                icon={getSectionIcon("Risk")}
              />
              <SectionCard
                id="toc-next-steps"
                title="Next Steps / Conditions"
                summary={nextSteps.summary}
                keyInsights={toList(nextSteps.key_insight)}
                warnings={toList(nextSteps.warnings)}
                markdown={nextSteps.markdown}
                icon={getSectionIcon("Conditions")}
              />
              <SectionCard
                id="toc-auditing"
                title="Auditing"
                summary={auditing.summary}
                keyInsights={toList(auditing.key_insight)}
                warnings={toList(auditing.warnings)}
                markdown={auditing.markdown}
                icon={getSectionIcon("Audit")}
              />
              <SectionCard
                id="toc-forensic"
                title="Forensic Accounting"
                summary={forensic.summary}
                keyInsights={toList(forensic.key_insight)}
                warnings={toList(forensic.warnings)}
                markdown={forensic.markdown}
                icon={getSectionIcon("Forensic")}
              />

              {references.length > 0 && (
                <div className="border border-slate-200 rounded-xl p-5 bg-white shadow-sm" id="toc-references">
                  <h3 className="text-lg font-bold text-slate-900 mb-3">References</h3>
                  <ul className="text-sm text-slate-700 list-disc pl-5 space-y-1">
                    {references.map((ref, idx) => (
                      <li key={`ref-${idx}`}>{ref}</li>
                    ))}
                  </ul>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnderwritingReportModal;
